import React, { useState } from "react";
import { cn } from "@/lib/utils";

export interface BarSeries {
  key: string;
  name: string;
  color: string;
}

export interface GroupedBarDatum {
  category: string;
  [seriesKey: string]: any;
}

interface GroupedBarChartProps {
  data: GroupedBarDatum[];
  series: BarSeries[];
  height?: number;
  formatValue?: (v: number) => string;
  className?: string;
}

export function GroupedBarChart({
  data,
  series,
  height = 200,
  formatValue = (v) => v.toLocaleString("en-IN"),
  className,
}: GroupedBarChartProps) {
  const [hoveredItem, setHoveredItem] = useState<{
    category: string;
    seriesName: string;
    value: number;
    color: string;
  } | null>(null);

  if (!data || data.length === 0 || !series || series.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-48 text-xs text-muted-foreground", className)}>
        No activity data available
      </div>
    );
  }

  // Find max value across all series
  let max = 0;
  data.forEach((d) => {
    series.forEach((s) => {
      const v = Number(d[s.key]) || 0;
      if (v > max) max = v;
    });
  });
  if (max === 0) max = 10;

  return (
    <div className={cn("space-y-3 w-full select-none", className)}>
      {/* Legend */}
      <div className="flex items-center justify-end gap-3 flex-wrap text-xs">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 font-medium text-muted-foreground text-[11px]">
            <span className="size-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
            <span>{s.name}</span>
          </div>
        ))}
      </div>

      {/* Chart Canvas */}
      <div
        className="flex items-end justify-between gap-3 sm:gap-6 pt-4 border-b border-border/80 px-2"
        style={{ height }}
        onMouseLeave={() => setHoveredItem(null)}
      >
        {data.map((item, idx) => (
          <div
            key={item.category + idx}
            className="flex-1 flex flex-col items-center justify-end h-full min-w-0"
          >
            {/* Bar cluster */}
            <div className="flex items-end justify-center gap-1 sm:gap-1.5 w-full h-full pb-1">
              {series.map((s) => {
                const val = Number(item[s.key]) || 0;
                const barHeightPct = Math.max(4, (val / max) * 100);

                return (
                  <div
                    key={s.key}
                    tabIndex={0}
                    onMouseEnter={() =>
                      setHoveredItem({
                        category: item.category,
                        seriesName: s.name,
                        value: val,
                        color: s.color,
                      })
                    }
                    className="flex-1 max-w-[20px] rounded-t-sm transition-all duration-150 cursor-pointer hover:brightness-110 hover:shadow-md"
                    style={{
                      height: `${barHeightPct}%`,
                      backgroundColor: s.color,
                    }}
                  />
                );
              })}
            </div>

            {/* X-axis label */}
            <span
              className="text-[11px] font-medium text-muted-foreground truncate w-full text-center mt-1.5"
              title={item.category}
            >
              {item.category}
            </span>
          </div>
        ))}
      </div>

      {/* Hover info banner */}
      <div className="h-5 text-center text-xs">
        {hoveredItem ? (
          <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
            <span className="size-2 rounded-full" style={{ backgroundColor: hoveredItem.color }} />
            <strong className="text-foreground">{hoveredItem.category}:</strong>
            <span>{hoveredItem.seriesName} — {formatValue(hoveredItem.value)}</span>
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground">Hover over any bar to view exact counts</span>
        )}
      </div>
    </div>
  );
}

